import { CodeResponse, useGoogleLogin } from "@react-oauth/google";
import React from "react";
import toast from "react-hot-toast";
import { AuthUser, useAuth } from "../../context/AuthContext";
import { trackEvent } from "../../lib/analytics";
import api from "../../lib/api";
import { getErrorMessage } from "../../lib/apiError";
import { GoogleButton } from "./GoogleLoginButton.styles";

const GoogleLoginButton: React.FC = () => {
  const { setAuth } = useAuth();

  const onSuccess = (tokenResponse: CodeResponse) => {
    const handleLogin = async () => {
      try {
        // Backend expects a "code" for access token exchange OR idToken path.
        // We'll use the code flow to call POST /v1/google-login-web
        const code = tokenResponse?.code as string;
        if (!code) {
          trackEvent("login_failed", { method: "google", reason: "missing_code" });
          toast.error("Failed to login with Google. Please try again.");
          return;
        }

        const { data } = await api.post<{
          token: string;
          user: AuthUser;
        }>("/auth/google-login-web", {
          code,
          firstName: "",
          lastName: "",
        });

        setAuth({ token: data.token, user: data.user });
        trackEvent("login_success", { method: "google" });
        toast.success("Successfully logged in!");
      } catch (e) {
        console.error("Google login error:", e);
        trackEvent("login_failed", { method: "google", reason: "exception" });
        const errorMessage = getErrorMessage(e, "Failed to login with Google. Please try again.");
        toast.error(errorMessage);
      }
    };

    handleLogin();
  };

  const onError = () => {
    trackEvent("login_failed", { method: "google", reason: "oauth_error" });
    toast.error("Google login was cancelled or failed. Please try again.");
  };

  const login = useGoogleLogin({
    onSuccess,
    onError,
    flow: "auth-code",
    scope: "openid email profile",
  });

  return (
    <GoogleButton onClick={() => login()}>
      Continue with Google
    </GoogleButton>
  );
};

export default GoogleLoginButton;
